// Hook customizado para gerenciar estado e lógica do Register
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { registerService } from "../services/registerService";
import { storageService } from "../services/storageService";
import { findPlot, plotExistsInAllPlots } from "../utils/helpers";
import {
  CATERPILLAR_PLAGUES,
  BEETLE_PLAGUES,
  PSILIDEO_PLAGUES,
  LEPTOCYBE_PLAGUES,
  MESSAGES,
} from "../utils/constants";
import { isBracellEnterprise, BRACELL_ENTERPRISE_KEY } from "../config/bracellRegistryProfile";
import { buildBracellDadosPayload } from "../utils/buildBracellDadosPayload";
import logger from "../../../utils/logger";
import { isMockMode, generateMockPlots } from "../../../utils/mockData";

/**
 * Hook para gerenciar estado e lógica do Register
 * @returns {Object} Estado e funções do Register
 */
export const useRegister = () => {
  // Estados principais
  const [informationPlague, setInformationPlague] = useState({});
  const [containPlague, setContainPlague] = useState(false);
  const [dataBasic, setDataBasic] = useState({});
  const [informationArea, setInformationArea] = useState({});
  const [levelInfestation, setLevelInfestation] = useState({});
  const [dataForm, setDataForm] = useState({});
  const [allPlots, setAllPlots] = useState([]);
  const [plot, setPlot] = useState({});
  const [loading, setLoading] = useState(false);
  const [auxValidator, setAuxValidator] = useState(false);
  const [bracellPlantCount, setBracellPlantCount] = useState(5);

  // Refs para controle de execução única
  const auxRef = useRef(0);

  // Parcelas calculadas a partir da área do talhão (somente BRACELL)
  const parcelas = (() => {
    if (!isBracellEnterprise()) return 2;
    const a = parseFloat(plot?.area);
    if (!Number.isFinite(a) || a <= 0) return 2;
    return Math.min(8, Math.max(2, Math.round(a / 10)));
  })();

  /**
   * Obtém o tipo de praga baseado no nome
   * @param {string} plagueName - Nome da praga
   * @returns {string} Tipo de praga ('caterpillar', 'beetle', 'psilide', '')
   */
  const getPlagueType = useCallback((plagueName) => {
    if (!plagueName) return "";

    if (CATERPILLAR_PLAGUES.includes(plagueName)) {
      return "caterpillar";
    } else if (BEETLE_PLAGUES.includes(plagueName)) {
      return "beetle";
    } else if (PSILIDEO_PLAGUES.includes(plagueName)) {
      return "psilide";
    } else if (LEPTOCYBE_PLAGUES.includes(plagueName)) {
      return "leptocybe";
    }

    return "";
  }, []);

  /**
   * Formata data atual no formato YYYY-MM-DD
   * @returns {string} Data formatada
   */
  const getDate = useCallback(() => {
    const date = new Date();
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString();
    const day = date.getDate().toString();
    return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }, []);

  /**
   * Carrega plots da API
   * Primeiro verifica localStorage, só faz requisição se necessário
   */
  const loadPlots = useCallback(async () => {
    // Verificar se está em modo mock
    if (isMockMode()) {
      logger.info("Modo mock: gerando plots mock");
      const region = storageService.getRegion();
      if (region) {
        const mockPlots = generateMockPlots(region);
        setAllPlots(mockPlots);
        storageService.setPlots(mockPlots);
        logger.success("Plots mock carregados", { count: mockPlots.length });
      }
      return;
    }

    // Primeiro, tentar carregar do localStorage
    const cachedPlots = storageService.getPlots();
    if (cachedPlots && Array.isArray(cachedPlots) && cachedPlots.length > 0) {
      logger.debug("Usando plots do localStorage", {
        count: cachedPlots.length
      });
      setAllPlots(cachedPlots);
      return; // Não fazer requisição se já temos plots em cache
    }

    // Só fazer requisição se não houver plots em cache
    setLoading(true);
    try {
      const plots = await registerService.getPlots();
      setAllPlots(plots);
    } catch (error) {
      logger.error("Erro ao carregar plots", error);
      // Não mostrar toast de erro se for erro de rede/CORS - pode ser temporário
      if (error.code !== 'ERR_NETWORK' && error.response?.status !== 500) {
        toast.error(MESSAGES.PLOTS_LOAD_ERROR);
      }
      // Tentar usar plots do localStorage mesmo em caso de erro
      const fallbackPlots = storageService.getPlots();
      if (fallbackPlots && fallbackPlots.length > 0) {
        setAllPlots(fallbackPlots);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Handler para mudança de informações da área
   * @param {Object} updateInformationArea - Informações da área atualizadas
   */
  const handleChangeInformationArea = useCallback((updateInformationArea) => {
    const region = storageService.getRegion();
    let plotFound = false;

    // Tentar usar allPlots do estado primeiro
    let plotsToSearch = allPlots;

    // Se allPlots não for válido, tentar carregar do localStorage
    if (!plotsToSearch || !Array.isArray(plotsToSearch) || plotsToSearch.length === 0) {
      plotsToSearch = storageService.getPlots();
      if (plotsToSearch.length > 0) {
        setAllPlots(plotsToSearch);
      }
    }

    // Buscar plot
    if (plotsToSearch && Array.isArray(plotsToSearch) && plotsToSearch.length > 0) {
      const foundPlot = findPlot(plotsToSearch, updateInformationArea.plot, region);
      
      if (foundPlot) {
        storageService.setRegisterPlotAlternate(foundPlot);
        setPlot(foundPlot);
        plotFound = true;
      }
    }

    // Se não encontrou no allPlots, verificar no register-plot-alternate
    if (!plotFound && updateInformationArea.plot) {
      const alternatePlot = storageService.getRegisterPlotAlternate();
      if (alternatePlot && 
          String(alternatePlot.plot) === String(updateInformationArea.plot) &&
          alternatePlot.region === region) {
        setPlot(alternatePlot);
        plotFound = true;
      }
    }

    setInformationArea(updateInformationArea);
  }, [allPlots]);

  /**
   * Handler para mudança de informações da praga
   * @param {string} updateInformationPlague - JSON string com informações da praga
   */
  const handleChangeInformationPlague = useCallback((updateInformationPlague) => {
    // Validar se o valor não está vazio
    if (!updateInformationPlague || updateInformationPlague === "") {
      return;
    }

    try {
      const parsed = JSON.parse(updateInformationPlague);
      
      // Validar se o objeto parseado tem as propriedades necessárias
      if (parsed && parsed.name && parsed.scientific) {
        setInformationPlague(parsed);
        setContainPlague(true);
        logger.debug("Praga selecionada:", parsed.name);
      } else {
        logger.warn("Objeto de praga inválido:", parsed);
      }
    } catch (error) {
      logger.error("Erro ao parsear informações da praga", {
        error: error.message,
        value: updateInformationPlague
      });
    }
  }, []);

  /**
   * Handler para mudança de dados básicos
   * @param {Object} updatedInformationBasic - Dados básicos atualizados
   */
  const handleChangeInformationDataBasic = useCallback((updatedInformationBasic) => {
    setDataBasic(updatedInformationBasic);
  }, []);

  /**
   * Handler para mudança de infestação
   * @param {Object} information - Informações de infestação
   */
  const handleChangeInfestation = useCallback((information) => {
    setLevelInfestation(information);
  }, []);

  /**
   * Handler para salvar registro
   */
  const handleClickSaveRegister = useCallback(async () => {
    if (!containPlague || !informationPlague.name) {
      toast.warning("Selecione uma praga antes de salvar");
      return;
    }

    try {
      const currentRegion = storageService.getRegion();
      const plotToValidate = informationArea?.plot;
      const plotsToValidate =
        Array.isArray(allPlots) && allPlots.length > 0 ? allPlots : storageService.getPlots();
      const validPlot = plotExistsInAllPlots(plotsToValidate, plotToValidate, currentRegion);

      if (!validPlot) {
        toast.warning(`Talhão com o nome ${plotToValidate || ""} não existe.`);
        return;
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const plagueType = getPlagueType(informationPlague.name);

      if (!plagueType) {
        toast.warning("Esta praga não é suportada no registro.");
        return;
      }

      let dadosPayload = dataForm;

      if (isBracellEnterprise()) {
        if (!Array.isArray(dataForm) || dataForm.length === 0) {
          toast.warning("Preencha as amostras antes de salvar.");
          return;
        }
        dadosPayload = buildBracellDadosPayload(plagueType, dataForm);
      }

      const probRaw =
        levelInfestation.prob !== undefined && levelInfestation.prob !== null
          ? Number(levelInfestation.prob)
          : 0;

      const config = {
        date: getDate(),
        monitor: user?.user || "",
        region: plot.region || dataBasic.region,
        plot: plot.plot || informationArea.plot,
        plague_type: plagueType,
        plague_name: informationPlague.name,
        scientific_name: informationPlague.scientific,
        diagnosis: levelInfestation.diagnostic,
        prob: parseFloat((Number.isFinite(probRaw) ? probRaw : 0).toFixed(2)),
        dados: dadosPayload,
      };

      if (isBracellEnterprise()) {
        config.enterprise = BRACELL_ENTERPRISE_KEY;
        const areaNum = parseFloat(plot?.area);
        config.parcelas = Number.isFinite(areaNum) && areaNum > 0
          ? Math.min(8, Math.max(2, Math.round(areaNum / 10)))
          : 2;
      }

      // Verificar se está em modo mock
      if (isMockMode()) {
        // Modo mock: não enviar para API, apenas mostrar mensagem
        logger.info("Modo mock: registro não será enviado para API");
        await registerService.saveRegister([config]);
        toast.success(MESSAGES.REGISTER_SUCCESS);
        
        // Disparar evento para recarregar dados do dashboard
        window.dispatchEvent(new CustomEvent("registryCreated", {
          detail: { message: "Novo registro criado com sucesso" }
        }));
      } else if (navigator.onLine) {
        // Modo online
        await registerService.saveRegister([config]);
        toast.success(MESSAGES.REGISTER_SUCCESS);
        
        // Disparar evento para recarregar dados do dashboard
        window.dispatchEvent(new CustomEvent("registryCreated", {
          detail: { message: "Novo registro criado com sucesso" }
        }));
      } else {
        // Modo offline
        const registers = storageService.getRegistersToInvite();
        registers.push(config);
        storageService.setRegistersToInvite(registers);
        toast.info(MESSAGES.REGISTER_OFFLINE_SUCCESS);
      }

      // Limpar formulário
      setInformationPlague({});
      setContainPlague(false);
      setBracellPlantCount(5);
      setAuxValidator(prev => !prev);
    } catch (error) {
      logger.error("Erro ao salvar registro", error);
      toast.error(MESSAGES.REGISTER_ERROR);
    }
  }, [
    containPlague,
    informationPlague,
    allPlots,
    plot,
    dataBasic,
    informationArea,
    levelInfestation,
    dataForm,
    getPlagueType,
    getDate
  ]);

  /**
   * Envia registros offline quando volta online
   */
  const sendOfflineRegisters = useCallback(async () => {
    if (auxRef.current >= 1) return;

    const registers = storageService.getRegistersToInvite();
    if (!registers || registers.length === 0) return;

    auxRef.current += 1;

    // Verificar se está em modo mock
    if (isMockMode()) {
      logger.info("Modo mock: interceptando envio de registros offline");
      try {
        await registerService.sendOfflineRegisters(registers);
        storageService.removeRegistersToInvite();
      } catch (error) {
        logger.error("Erro ao processar registros mock offline", error);
        storageService.removeRegistersToInvite();
      }
      return;
    }

    try {
      await registerService.sendOfflineRegisters(registers);
      storageService.removeRegistersToInvite();
    } catch (error) {
      logger.error("Erro ao enviar registros offline", error);
      storageService.removeRegistersToInvite();
    }
  }, []);

  // Carregar plots ao montar componente
  useEffect(() => {
    loadPlots();
  }, [loadPlots]);

  // Enviar registros offline quando voltar online
  useEffect(() => {
    if (navigator.onLine && auxRef.current === 0) {
      sendOfflineRegisters();
    }
  }, [sendOfflineRegisters]);

  // Limpar plot alternativo e resetar estado ao montar componente
  useEffect(() => {
    storageService.removeRegisterPlotAlternate();
    
    // Resetar estado de praga ao montar (evitar estado persistente de navegações anteriores)
    setInformationPlague({});
    setContainPlague(false);
    setLevelInfestation({});
    setDataForm({});
    setBracellPlantCount(5);
  }, []);

  return {
    // Estados
    informationPlague,
    containPlague,
    dataBasic,
    informationArea,
    levelInfestation,
    dataForm,
    allPlots,
    plot,
    loading,
    auxValidator,
    bracellPlantCount,
    setBracellPlantCount,
    parcelas,

    // Handlers
    handleChangeInformationArea,
    handleChangeInformationPlague,
    handleChangeInformationDataBasic,
    handleChangeInfestation,
    handleClickSaveRegister,
    setDataForm
  };
};

